import Client from "../../../Client";
import { approveLGUVisitsSubmissionData, deleteLGUVisitsSubmissionData, getLGUVisitsDraftData, getLGUVisitsSubmissionData, publishLGUVisitsData, submitLGUVisitsData, unapproveLGUVisitsSubmissionData, updateLGUVisitsDraftData, updateLGUVisitsSubmissionData } from "../../../services/api/data/sameday";
import { AlertDrawerController } from "../../../views/sections/sidebar/AlertDrawer";
import { Notifier } from "../../conductor";
import React from "react";
import Exceptions from "../../../Exceptions";
import { wait } from "../../../misc/date";
import { MainRouter } from "../../../routes";
import { showModal } from "../../../views/widgets/modal";
import { ExcelFileImporter } from "../../../views/widgets/modals/import";
import { getLevel } from "../../../misc/account";
import { addOrEditAttraction } from "../../Attractions";
import { addAttraction, getLGUList, getProvinceList } from "../../../services/api/meta";
import { listToCollection } from "../../../misc/object";

export class SameDayLguController {
    constructor(options) {
        /** @type {"draft" | "submission"} */
        this.type = options.type;

        /** @type {{ lgu: string, id: string }} */
        this.reference = options.ref;

        const self = this;        
        this.notifiers = {
            ready: new Notifier(false),
            unauthorized: new Notifier(false),
            saving: new Notifier(false),
            deleting: new Notifier(false),
            approving: new Notifier(false),
            submitting: new Notifier(false),
            publishing: new Notifier(false),
            reloading: new Notifier(false),
            addingAttraction: new Notifier(false),
            reloaded: new Notifier(),
            lastUpdated: new Notifier(),
            alert: new AlertDrawerController()
        };

        this.canExit = false;

        this.status = {
            get ready() { return self.notifiers.ready.get() },
            get reloading() { return self.notifiers.reloading.get() },
            get unauthorized() { return self.notifiers.unauthorized.get() },
            get saving() { return self.notifiers.saving.get() },
            get deleting() { return self.notifiers.deleting.get() },
            get approving() { return self.notifiers.approving.get() },
            get submitting() { return self.notifiers.submitting.get() },
            get addingAttraction() { return self.notifiers.addingAttraction.get() },
            get publishing() { return self.notifiers.publishing.get() }
        };

        window.addEventListener("beforeunload", event => {
            if(!this.canExit) {
                event.preventDefault();
                event.returnValue = "1";
            }
        });

        this.initialize();
    }

    async initialize() {
        Client.ensureSignedIn();
        if(this.isInitialized) return; this.isInitialized = true;
        this.canEditSubmissions = Client.canEditSubmissions;

        try {
            this.collection = {
                province: listToCollection(await getProvinceList()),
                lgu: listToCollection(await getLGUList())
            };
            await this.loadData();
        } catch(e) {
            if(e.unauthorized) return this.notifiers.unauthorized.update(true);
        }
        
        this.notifiers.ready.update(true);
    }

    async loadData() {
        if(this.type === "draft") {
            const { lgu, month, year } = this.reference;
            this.data = await getLGUVisitsDraftData({ lgu, month, year });
            this.refreshForm();
        } else if(this.type === "submission") {
            const { submissionId } = this.reference;
            this.data = await getLGUVisitsSubmissionData(submissionId);
            this.refreshForm();
        } else {
            throw new Error("Invalid type");
        }
    }

    async refreshForm() {
        this.refs = this.data.attractions.map(attraction => ({
            id: attraction.id,
            refs: [ React.createRef(), React.createRef(), React.createRef(),
                React.createRef(), React.createRef(), React.createRef(),
                React.createRef(), React.createRef(), React.createRef() ]
        }));

        if(this.type === "submission") {
            this.reference.month = this.data.month;
            this.reference.year = this.data.year;
            this.status.approved = this.data.approvals[getLevel(Client.account.role)].some(a => a.by === Client.account.id);
            this.canApprove = Client.canApprove && (Client.account.role === "admin" || !Client.isSelf(this.data.author.id));
        }


        this.notifiers.reloaded.notify();
    }

    async refresh(closeAlert = true) {
        const { reloading, alert } = this.notifiers;
        reloading.update(true);

        if(closeAlert) alert.close();

        try {
            await this.loadData();
        } catch(e) {
            const semantics = Exceptions.getARESemantics(e);
            alert.alert(semantics.message, { status: "error", title: semantics.title, retry: () => this.refresh() });
        }

        reloading.update(false);
    }
    
    async changeDate(month, year) {
        this.reference.month = month;
        this.reference.year = year;
        await this.refresh();
        MainRouter.pushPath(`/forms/sameday/lgu/${this.reference.lgu}/draft/${month}/${year}`)
    }

    collectAttractionsData() {
        return this.refs.map(attraction => {
            const ref = (index) => Number(attraction.refs[index].current.value);
            return {
                id: attraction.id,
                counts: {
                    domestic: { 
                        local: { male: ref(0), female: ref(1), ngd: ref(2) },
                        external: { male: ref(3), female: ref(4), ngd: ref(5) }
                    },
                    foreign: { male: ref(6), female: ref(7), ngd: ref(8) }
                }
            }
        })
    }

    async save() {
        const { saving, alert } = this.notifiers;
        saving.update(true);
        alert.close();

        try {
            const attractions = this.collectAttractionsData();
            const { lgu, month, year, submissionId } = this.reference;

            // return showModal({ title: "Hello world", body: "This is a sample modal", footer: "Let it pass" });

            await wait(300);
            const updated = this.type === "draft" ? 
                await updateLGUVisitsDraftData({ lgu, month, year, attractions }) :
                await updateLGUVisitsSubmissionData({ submissionId, attractions });
            
            if(this.type === "draft") {
                this.data.lastUpdated = updated.lastUpdated;
            } else {
                this.data.lastUpdate = updated.lastUpdate;
            }

            this.notifiers.lastUpdated.notify();


            alert.alert(this.type === "draft" ? 
                "Draft saved successfully." : "Submission updated successfully.");
        } catch(e) {
            console.log(e);
            const { title, message } = Exceptions.getARESemantics(e);
            alert.alert(message, { title, status: "error", retry: () => this.save() });
        }
        
        saving.update(false);
    }

    async submit(data) {
        const { alert, submitting } = this.notifiers;
        const { remarks } = data;

        const returned = await showModal({ 
            title: "Verify Submission", 
            body: <span>
                You are about to submit this data. 
                Please confirm this action by  
                <b> typing in the password</b> to this account.
            </span>, footer: [],
            withPassword: true
        });

        if(!returned) return;
        const password = returned.password;

        submitting.update(true);

        try {
            const { lgu, month, year } = this.reference;
            const attractions = this.collectAttractionsData();
            const submission = await submitLGUVisitsData({ lgu, month, year, attractions, remarks, password });
            alert.alert(`Document submitted successfully. Ref id: ${submission.refId}`);

        } catch(e) {
            const { title, message } = Exceptions.getARESemantics(e);
            alert.alert(message, { title, status: "error", retry: () => this.submit() });
        }

        submitting.update(false);
    }

    async publish() {
        const { alert, publishing } = this.notifiers;

        const returned = await showModal({ 
            title: "Verify Publishing", 
            body: <span>
                You are about to <b>publish</b> this data. 
                Please confirm this action by  
                <b> typing in the password</b> to this account.
            </span>, footer: [],
            withPassword: true
        });

        if(!returned) return;
        const password = returned.password;

        publishing.update(true);

        try {
            const { lgu, month, year } = this.reference;
            const attractions = this.collectAttractionsData();
            // await wait(1500);
            await publishLGUVisitsData({ lgu, month, year, attractions, password });
            alert.alert("Document published successfully.");

        } catch(e) {
            const { title, message } = Exceptions.getARESemantics(e);
            alert.alert(message, { title, status: "error", retry: () => this.publish() });
        }

        publishing.update(false);
    }

    async delete() {
        if(this.type === "draft") return;

        const { deleting, alert } = this.notifiers;

        try {
            const returned = await showModal({ 
                title: "Verify Deletion", 
                titleColor: "red",
                body: <span>
                    You are about to <b>delete</b> this data. 
                    Please confirm this action by  
                    <b> typing in the password</b> to this account.
                </span>, footer: [],
                withPassword: true
            });
    
            if(!returned) return;
            const password = returned.password;

            
            deleting.update(true);
            await deleteLGUVisitsSubmissionData({ submissionId: this.reference.submissionId, password });
            
            const { month, year } = this.reference;
            MainRouter.shift(`/forms/sameday/lgu/${this.reference.lgu}/submissions/${month}/${year}`);
        } catch(e) {
            console.log(e);
            const { title, message } = Exceptions.getARESemantics(e);
            alert.alert(message, { title, status: "error", retry: () => this.delete() });
        }

        deleting.update(false);
    }


    viewSubmissions() {
        const { month, year } = this.reference;
        MainRouter.goto(`/forms/sameday/lgu/${this.reference.lgu}/submissions/${month}/${year}`);
    }

    async toggleApprove() {
        const { alert, approving, reloaded } = this.notifiers;
        const toApprove = !this.status.approved;

        const returned = await showModal({ 
            title: "Verify Approval", 
            body: <span>
                You are about to <b>{toApprove ? "approve" : "unapprove"}</b> this form. 
                Please confirm this action by  
                <b> typing in the password</b> to this account.
            </span>, footer: [],
            withPassword: true
        });

        if(!returned) return;
        const password = returned.password;

        approving.update(true);

        try {
            const { submissionId } = this.reference;
            let approvals = [];
            if(toApprove) {
                approvals = await approveLGUVisitsSubmissionData({ submissionId, password });
                alert.alert("Document approved successfully.");
            } else {
                approvals = await unapproveLGUVisitsSubmissionData({ submissionId, password });
                alert.alert("Document unapproved successfully.");
            }

            this.status.approved = toApprove;
            this.data.approvals = approvals;
            reloaded.notify();

        } catch(e) {
            const { title, message } = Exceptions.getARESemantics(e);
            alert.alert(message, { title, status: "error", retry: () => this.toggleApprove() });
        }

        approving.update(false);
    }

    async import() {
        const controller = {};

        const onExtract = (extracted) => {
            const extras = [];

            for(const e of extracted) {
                const attraction = this.data.attractions.find(a => a.name === e.name);
                if(attraction) {
                    attraction.counts = e.counts;
                } else extras.push(e);
            }

            controller.close();
            this.refreshForm();
            this.notifiers.alert.alert("Data imported to form successfully.");
        };

        await showModal({ 
            title: "Import Excel Sheet", 
            body: <ExcelFileImporter onExtract={onExtract} />,
            footer: []
        }, controller);
    }

    async addAttraction() {
        const { notifiers: { alert, addingAttraction } } = this;
        const addable = await addOrEditAttraction(null, { 
            lgu: this.reference.lgu, collection: this.collection, lguFixed: true });

        if(addable) {
            addingAttraction.update(true);
            alert.alert("Adding attraction...", { status: "progress" });

            try {
                await addAttraction(addable);
                alert.alert("Attraction added successfully!");
            } catch(e) {
                const { title, message } = Exceptions.getARESemantics(e);
                alert.alert(message, { title, status: "error" });
            }


            this.refresh(false);
            addingAttraction.update(false);
        }
    }
}